/**
 * Quiz Type Definitions
 * Defines the structure for quizzes, questions, and responses
 */
export enum QuizStatus {
  Draft = 'draft',
  Published = 'published',
  Archived = 'archived',
}

export enum QuizResponseStatus {
  InProgress = 'in_progress',
  Completed = 'completed',
  Abandoned = 'abandoned',
}

export const QuestionTypeConfig = {
  SHORT_ANSWER: { value: 'short_answer', label: 'Short Answer' },
  PARAGRAPH: { value: 'paragraph', label: 'Paragraph' },
  SINGLE_SELECT: { value: 'single_select', label: 'Single Select' },
  MULTI_SELECT: { value: 'multi_select', label: 'Multiple Select', limit: -1 },
  FILE_UPLOAD: { value: 'file_upload', label: 'File Upload' },
  NUMERIC: { value: 'numeric', label: 'Numeric' },
  DATE: { value: 'date', label: 'Date' },
  BOOLEAN: { value: 'boolean', label: 'Yes/No Toggle' },
  WAIVER: { value: 'waiver', label: 'Waiver' },
} as const;

export const QuestionTypes = {
  SHORT_ANSWER: QuestionTypeConfig.SHORT_ANSWER.value,
  PARAGRAPH: QuestionTypeConfig.PARAGRAPH.value,
  SINGLE_SELECT: QuestionTypeConfig.SINGLE_SELECT.value,
  MULTI_SELECT: QuestionTypeConfig.MULTI_SELECT.value,
  FILE_UPLOAD: QuestionTypeConfig.FILE_UPLOAD.value,
  NUMERIC: QuestionTypeConfig.NUMERIC.value,
  DATE: QuestionTypeConfig.DATE.value,
  BOOLEAN: QuestionTypeConfig.BOOLEAN.value,
  WAIVER: QuestionTypeConfig.WAIVER.value,
} as const;

export type QuestionType = typeof QuestionTypes[keyof typeof QuestionTypes];

export const QuestionTypeItems = Object.values(QuestionTypeConfig);

export interface QuestionOption {
  id: string;
  label: string;
  value: string;
  score?: number; // For scoring-based quizzes
}

export interface Question {
  id: string;
  quizId: string;
  sequence: number;
  name: string;
  type: QuestionType;
  text: string;
  detail?: string; // Additional explanation or helper text
  required: boolean;
  options?: QuestionOption[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    maxLength?: number;
  };
  metadata?: Record<string, unknown>; // For custom question properties
}

export interface Quiz {
  id: string;
  name: string;
  description: string;
  version: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string; // Admin user ID
  status: QuizStatus;
  questions: Question[];
  settings?: {
    allowBack: boolean;
    showProgress: boolean;
    shuffleQuestions: boolean;
    timeLimit?: number; // in seconds
    passingScore?: number;
    maxAttempts?: number;
  };
  metadata?: Record<string, unknown>; // For custom quiz properties
}

export interface QuizAnswer {
  questionId: string;
  value: string | string[] | number | File | null;
  answeredAt: string;
}

export interface QuizResponse {
  id: string;
  quizId: string;
  quizVersion: string;
  userId: string; // Email or internal ID
  status: QuizResponseStatus;
  startedAt: string;
  completedAt?: string;
  timeSpent: number; // in seconds
  answers: QuizAnswer[];
  score?: number;
  result?: string; // Profile type or result category
  metadata?: {
    userAgent?: string;
    ipAddress?: string;
    demographics?: Record<string, unknown>;
  };
}

// Legacy type mapping for backward compatibility
export interface LegacyQuizResponse {
  [key: string]: string | string[];
}

export interface LegacyQuizSubmission {
  responses: LegacyQuizResponse;
  profileType: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  ageRange?: string;
  referralSource?: string;
  connectionType?: string[];
  completedAt: Date;
  timeToComplete: number;
  submissionId?: string;
}
